import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import {
  Control,
  FieldError,
  FieldPath,
  FieldPathValue,
  FieldValues,
  get,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineDocumentText,
  HiOutlineExclamation,
  HiX,
} from 'react-icons/hi';
import { useCreateUploadUrlMutation } from '@src/generated/graphql';
import { useUpload } from '@src/hooks/useUpload';
import { formatError } from '@src/services/error-formatter';
import { logError } from '@src/services/error-logger';
import Button from '../Button';
import FormError from '../FormError';
import FormLabel from '../FormLabel';
import LinkButton from '../LinkButton';

interface FileUploadInput {
  id: string;
  name: string;
}

interface Props {
  className?: string;
  disabled?: boolean;
  name?: string;
  onChange?: (value: FileUploadInput | null) => void;
  onBlur?: () => void;
  value?: FileUploadInput;
  placeholder?: string;
  category: string;
  accept?: DropzoneOptions['accept'];
}

const FileInput = function FileInput({
  className,
  name,
  onChange,
  value,
  category,
  accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf'],
  },
}: Props): JSX.Element {
  const [validationError, setValidationError] = useState<
    string | null | undefined
  >(null);
  const [createUploadUrl] = useCreateUploadUrlMutation();
  const { t } = useTranslation();

  const { isUploading, error, progress, uploadFile, cancelUpload } =
    useUpload<FileUploadInput>({
      getUploadParameters: async (fileToUpload) => {
        const contentType = fileToUpload.type || 'application/octet-stream';
        const { data } = await createUploadUrl({
          variables: {
            input: {
              category,
              fileName: fileToUpload.name,
              fileSize: fileToUpload.size,
              // TODO: Figure out what to do if type is blank
              contentType,
            },
          },
        });
        if (!data) {
          throw new Error(`Could not create upload url`);
        }
        const {
          createPresignedUploadUrl: { url, fields, method, file },
        } = data;
        return { url, fields: fields || [], method, meta: file };
      },
      onUploaded: (file) => {
        const uploadedFile = file.meta;
        if (onChange) {
          onChange({ name: uploadedFile.name, id: uploadedFile.id });
        }
      },
      onError: (err) => {
        logError(err);
      },
      trackProgress: true,
    });

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      // mirrors backend limit in backend/src/modules/storage/utils/upload.ts
      if (file.name.length > 128) {
        setValidationError(
          t(
            'file_input.errors.file_name_too_long',
            'File name is too long. Please rename it and try again.',
          ),
        );
        return;
      }
      uploadFile(file);
    },
    [t, uploadFile],
  );

  // PNG, JPG, PDF up to 10MB
  const { getRootProps, getInputProps, inputRef, isDragActive } = useDropzone({
    accept,
    onDropAccepted,
    onDropRejected(files) {
      const fileIsOfInvalidType = (files[0].errors || []).find(
        (e) => e.code === 'file-invalid-type',
      );
      const fileIsTooLarge = (files[0].errors || []).find(
        (e) => e.code === 'file-too-large',
      );

      if (fileIsOfInvalidType) {
        setValidationError(
          t(
            'file_input.errors.unsupported_file_type',
            'File type not supported.',
          ),
        );
      } else if (fileIsTooLarge) {
        setValidationError(
          t(
            'file_input.errors.file_too_big',
            'File size cannot be larger than 10MB.',
          ),
        );
      }
    },
    multiple: false,
    maxFiles: 1,
    maxSize: 10000000, // 10mb in bytes
  });

  const handleRemove = (): void => {
    if (onChange) onChange(null);
    cancelUpload();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleCancel = (): void => {
    cancelUpload();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (validationError) {
      setValidationError(null);
    }
  };

  const percentage = Math.round(progress * 100);

  return (
    <div className={clsx('max-w-md', className)}>
      {(() => {
        if (value) {
          return (
            <div className="flex min-h-[140px] flex-col items-center space-y-2 rounded-md bg-blue-50 p-4">
              <LinkButton className="self-end" onClick={handleRemove}>
                <HiX className="text-cool-gray-500 h-4 w-4" />
              </LinkButton>
              <HiOutlineDocumentText className="h-8 w-8 text-blue-700" />
              <div className="text-sm font-medium leading-4">{value.name}</div>
            </div>
          );
        }

        if (error || validationError) {
          return (
            <div className="flex min-h-[140px] flex-col items-center space-y-4 rounded-md bg-red-50 p-4">
              <HiOutlineExclamation className="h-8 w-8 text-red-700" />
              <div className="text-center font-medium leading-4 text-red-700">
                {validationError ||
                  formatError(
                    error,
                    t('file_input.errors.generic', 'Upload failed.'),
                  )}
              </div>
              <Button size="small" color="white" onClick={handleRemove}>
                {t('buttons.try_again', 'Try Again')}
              </Button>
            </div>
          );
        }

        if (isUploading) {
          return (
            <div className="mt-1 flex min-h-[140px] flex-col items-center justify-center space-y-2 rounded-md border-2 border-dashed border-gray-300 p-2">
              <CircularProgressbar
                className="h-12 w-12"
                value={percentage}
                text={`${percentage}%`}
              />
              <div className="text-sm">
                {t('file_input.upload_in_progress', 'Upload in progress')}
              </div>
              <Button onClick={handleCancel} color="white" size="small">
                {t('buttons.cancel', 'Cancel')}
              </Button>
            </div>
          );
        }

        return (
          <div
            {...getRootProps()}
            className={clsx(
              'mt-1 flex min-h-[140px] cursor-pointer justify-center rounded-md border-2 border-dashed px-6 pb-6 pt-5',
              {
                'border-blue-500': isDragActive,
                'border-gray-300': !isDragActive,
              },
            )}
          >
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <div>
                  <span className="font-medium text-blue-600 hover:text-blue-500">
                    {t('file_input.upload_prompt', 'Upload a file')}
                  </span>{' '}
                  <span>
                    {t(
                      'file_input.upload_prompt_drag_drop',
                      'or drag and drop',
                    )}
                  </span>
                  <input
                    type="file"
                    className="sr-only"
                    name={name}
                    {...getInputProps()}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {t(
                  'file_input.upload_restrictions',
                  'PNG, JPG, PDF up to 10MB',
                )}
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

interface FileInputLabelledProps extends Props {
  label?: string;
  error?: React.ReactNode;
}

FileInput.Labelled = function FileInputLabelled({
  label,
  className,
  error,
  ...rest
}: FileInputLabelledProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <div className={clsx('block', className)}>
      {label && <FormLabel>{label}</FormLabel>}
      <FileInput {...rest} />
      {error && <FormError>{error}</FormError>}
    </div>
  );
};

interface FileInputControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<FileInputLabelledProps, 'onChange' | 'value' | 'error'> {
  control: Control<TFieldValues>;
  name: TFieldName;
}

FileInput.LabelledController = function FileInputController<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  ...rest
}: FileInputControllerProps<TFieldValues, TFieldName>): JSX.Element {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });
  const error = get(errors, name) as FieldError | undefined;

  // TODO: Validate value is correct type
  const validatedValue = (value as FileUploadInput)?.id
    ? (value as FileUploadInput)
    : undefined;

  return (
    <FileInput.Labelled
      onChange={(newValue) =>
        onChange(newValue as FieldPathValue<TFieldValues, TFieldName>)
      }
      value={validatedValue}
      error={error?.message}
      {...rest}
    />
  );
};

export default FileInput;
